/**
 * Pedersen Distributed Key Generation ceremony — primitive operations.
 *
 * This module exposes per-round pure functions that each trustee runs
 * on their own device, plus an in-process simulator (`simulateDkg`)
 * useful for tests and the R1 smoke checkout.
 *
 * Out of scope here, deferred to follow-up commits:
 *  - per-contribution signatures (R2/R3 trustees sign via QES; pilot
 *    consultativ keeps unsigned transcripts, see docs/20 § 2 A).
 *  - share-package encryption to recipient's long-term key (pilot
 *    operator hand-carries plaintext shares, see docs/20 § 2 A).
 *  - complaint round adjudication (the verification primitive returns
 *    complaints; orchestrating the dealer's defence and final
 *    qualification of trustees is policy that lives outside this lib).
 */
import type {
  DkgRound1Contribution,
  DkgRound1Secret,
  DkgSharePackage,
  DkgTranscript,
  RistrettoPoint,
  Trustee,
  TrusteeSecretShare,
} from "../types";
import { GROUP_ORDER } from "../internal/encoding";
import {
  combineCommitments,
  evaluatePolynomial,
  feldmanCommitments,
  samplePolynomial,
  verifyShareAgainstFeldman,
} from "../internal/polynomial";

// ---------------------------------------------------------------------------
// Round 1 — dealing
// ---------------------------------------------------------------------------

export interface Round1Input {
  /** This trustee, the one running the round. */
  readonly self: Trustee;
  /** All trustees including self. Indices must be unique and >= 1. */
  readonly trustees: ReadonlyArray<Trustee>;
  /** Reconstruction threshold `t`. `1 <= t <= trustees.length`. */
  readonly threshold: number;
}

export interface Round1Output {
  /** Public Feldman commitments — published to every other trustee. */
  readonly contribution: DkgRound1Contribution;
  /** Private polynomial coefficients — persisted to a 0600 file by the CLI. */
  readonly secret: DkgRound1Secret;
  /**
   * One share per OTHER trustee (not for self — self's contribution is
   * recovered from `secret` at finalize time). For pilot consultativ
   * shares are plaintext; for binding scrutins this carries an encryption
   * envelope, see docs/20 § 2 A.
   */
  readonly sharesForOthers: ReadonlyArray<DkgSharePackage>;
}

export function dkgRound1(input: Round1Input): Round1Output {
  validateTrusteeSet(input.trustees, input.threshold);
  if (!input.trustees.some((t) => t.id === input.self.id)) {
    throw new Error(`self trustee ${input.self.id} not in trustees set`);
  }

  const coefficients = samplePolynomial(input.threshold);
  const commitments = feldmanCommitments(coefficients);

  const sharesForOthers: DkgSharePackage[] = [];
  for (const recipient of input.trustees) {
    if (recipient.id === input.self.id) continue;
    sharesForOthers.push({
      fromTrusteeId: input.self.id,
      toTrusteeId: recipient.id,
      share: evaluatePolynomial(coefficients, BigInt(recipient.index)),
    });
  }

  return {
    contribution: {
      trusteeId: input.self.id,
      feldmanCommitments: commitments,
    },
    secret: {
      trusteeId: input.self.id,
      polynomialCoefficients: coefficients,
    },
    sharesForOthers,
  };
}

// ---------------------------------------------------------------------------
// Round 2 — verification & complaint emission
// ---------------------------------------------------------------------------

export interface Round2Input {
  readonly self: Trustee;
  readonly trustees: ReadonlyArray<Trustee>;
  readonly threshold: number;
  /** All trustees' R1 contributions (including self's, ignored for self). */
  readonly contributions: ReadonlyArray<DkgRound1Contribution>;
  /** Shares received from others; one per OTHER trustee. */
  readonly receivedShares: ReadonlyArray<DkgSharePackage>;
}

export interface Round2Complaint {
  readonly againstTrusteeId: string;
  readonly reason: string;
}

export interface Round2Output {
  readonly complaints: ReadonlyArray<Round2Complaint>;
}

/**
 * For each other trustee i, find the share they sent to self and verify
 * it against their published Feldman commitments. Emit a complaint per
 * inconsistency. A missing share is also a complaint (the dealer didn't
 * deliver). Self's own contribution is not verified here; the dealer
 * trusts their own polynomial.
 */
export function dkgRound2Verify(input: Round2Input): Round2Output {
  validateTrusteeSet(input.trustees, input.threshold);
  if (!input.trustees.some((t) => t.id === input.self.id)) {
    throw new Error(`self trustee ${input.self.id} not in trustees set`);
  }

  const contributionByTrustee = new Map<string, DkgRound1Contribution>();
  for (const c of input.contributions) {
    if (contributionByTrustee.has(c.trusteeId)) {
      throw new Error(`duplicate contribution from trustee ${c.trusteeId}`);
    }
    contributionByTrustee.set(c.trusteeId, c);
  }

  const sharesByDealer = new Map<string, DkgSharePackage>();
  for (const s of input.receivedShares) {
    if (s.toTrusteeId !== input.self.id) {
      throw new Error(
        `received share addressed to ${s.toTrusteeId}, not self ${input.self.id}`,
      );
    }
    if (sharesByDealer.has(s.fromTrusteeId)) {
      throw new Error(`duplicate share from dealer ${s.fromTrusteeId}`);
    }
    sharesByDealer.set(s.fromTrusteeId, s);
  }

  const complaints: Round2Complaint[] = [];
  for (const dealer of input.trustees) {
    if (dealer.id === input.self.id) continue;

    const contribution = contributionByTrustee.get(dealer.id);
    if (!contribution) {
      complaints.push({
        againstTrusteeId: dealer.id,
        reason: "missing R1 contribution",
      });
      continue;
    }
    if (contribution.feldmanCommitments.length !== input.threshold) {
      complaints.push({
        againstTrusteeId: dealer.id,
        reason: `feldman commitments length ${contribution.feldmanCommitments.length} != threshold ${input.threshold}`,
      });
      continue;
    }

    const share = sharesByDealer.get(dealer.id);
    if (!share) {
      complaints.push({
        againstTrusteeId: dealer.id,
        reason: "missing share to self",
      });
      continue;
    }

    const ok = verifyShareAgainstFeldman(
      share.share,
      input.self.index,
      contribution.feldmanCommitments,
    );
    if (!ok) {
      complaints.push({
        againstTrusteeId: dealer.id,
        reason: "share inconsistent with Feldman commitments",
      });
    }
  }

  return { complaints };
}

// ---------------------------------------------------------------------------
// Finalize — combined PK + per-trustee verification keys + secret share
// ---------------------------------------------------------------------------

export interface FinalizeInput {
  readonly self: Trustee;
  /** Qualified set Q after complaint resolution; ordered however. */
  readonly qualifiedTrustees: ReadonlyArray<Trustee>;
  readonly threshold: number;
  /** Contributions from every qualified trustee (self included). */
  readonly contributions: ReadonlyArray<DkgRound1Contribution>;
  /** Self's private R1 state, needed for the f_self(self.index) component. */
  readonly mySecret: DkgRound1Secret;
  /** Shares received from other qualified trustees. */
  readonly receivedShares: ReadonlyArray<DkgSharePackage>;
}

export interface FinalizeOutput {
  readonly transcript: DkgTranscript;
  readonly mySecretShare: TrusteeSecretShare;
}

export function dkgFinalize(input: FinalizeInput): FinalizeOutput {
  validateTrusteeSet(input.qualifiedTrustees, input.threshold);
  const selfInQ = input.qualifiedTrustees.find((t) => t.id === input.self.id);
  if (!selfInQ) {
    throw new Error(`self trustee ${input.self.id} not in qualified set`);
  }
  if (input.mySecret.trusteeId !== input.self.id) {
    throw new Error("mySecret belongs to a different trustee");
  }

  const orderedTrustees = [...input.qualifiedTrustees].sort(
    (a, b) => a.index - b.index,
  );
  const contributionByTrustee = new Map<string, DkgRound1Contribution>();
  for (const c of input.contributions) {
    contributionByTrustee.set(c.trusteeId, c);
  }
  const orderedContributions: DkgRound1Contribution[] = [];
  for (const t of orderedTrustees) {
    const c = contributionByTrustee.get(t.id);
    if (!c) {
      throw new Error(`missing contribution for qualified trustee ${t.id}`);
    }
    if (c.feldmanCommitments.length !== input.threshold) {
      throw new Error(
        `trustee ${t.id} feldman commitments length ${c.feldmanCommitments.length} != threshold ${input.threshold}`,
      );
    }
    orderedContributions.push(c);
  }

  // Combined PK = sum of every dealer's constant-term commitment C_{i,0}.
  const combinedPublicKey = sumPoints(
    orderedContributions.map((c) => c.feldmanCommitments[0]!),
  );

  // Each trustee's verification key g^{x_j} =
  //   sum_{i ∈ Q} prod_{k=0}^{t-1} C_{i,k}^{j^k}
  const trusteesWithKeys: Trustee[] = orderedTrustees.map((t) => ({
    id: t.id,
    index: t.index,
    name: t.name,
    verificationKey: sumPoints(
      orderedContributions.map((c) =>
        combineCommitments(c.feldmanCommitments, BigInt(t.index)),
      ),
    ),
  }));

  // Self's secret share x_self =
  //   f_self(self.index)  +  sum_{i ∈ Q, i != self} share_{i→self}
  const selfShareFromOwnPoly = evaluatePolynomial(
    input.mySecret.polynomialCoefficients,
    BigInt(input.self.index),
  );

  const sharesByDealer = new Map<string, DkgSharePackage>();
  for (const s of input.receivedShares) {
    if (s.toTrusteeId !== input.self.id) {
      throw new Error(
        `received share addressed to ${s.toTrusteeId}, not self ${input.self.id}`,
      );
    }
    sharesByDealer.set(s.fromTrusteeId, s);
  }

  let secret = selfShareFromOwnPoly;
  for (const t of orderedTrustees) {
    if (t.id === input.self.id) continue;
    const share = sharesByDealer.get(t.id);
    if (!share) {
      throw new Error(
        `qualified trustee ${t.id} did not deliver a share to self`,
      );
    }
    secret = (secret + share.share) % GROUP_ORDER;
  }

  return {
    transcript: {
      threshold: input.threshold,
      trustees: trusteesWithKeys,
      feldmanCommitments: orderedContributions.map(
        (c) => c.feldmanCommitments,
      ),
      combinedPublicKey,
    },
    mySecretShare: {
      trusteeId: input.self.id,
      index: input.self.index,
      secret,
    },
  };
}

// ---------------------------------------------------------------------------
// Public verification — recompute combined PK from a transcript
// ---------------------------------------------------------------------------

/**
 * Verify the transcript's internal consistency and return the combined
 * election public key. Throws if the recorded combinedPublicKey does
 * not match the recomputed sum of trustee constant-term commitments,
 * or if any trustee's verificationKey does not match the recomputed
 * derivation from Feldman commitments.
 *
 * Pure function of the transcript bytes — no clock, no randomness.
 */
export function combinePublicKey(transcript: DkgTranscript): RistrettoPoint {
  if (transcript.feldmanCommitments.length !== transcript.trustees.length) {
    throw new Error("feldmanCommitments length != trustees length");
  }
  for (const row of transcript.feldmanCommitments) {
    if (row.length !== transcript.threshold) {
      throw new Error(
        `feldman row length ${row.length} != threshold ${transcript.threshold}`,
      );
    }
  }

  const recomputedPk = sumPoints(
    transcript.feldmanCommitments.map((row) => row[0]!),
  );
  if (!recomputedPk.equals(transcript.combinedPublicKey)) {
    throw new Error("combinedPublicKey does not match Feldman commitments");
  }

  for (const t of transcript.trustees) {
    const recomputedVk = sumPoints(
      transcript.feldmanCommitments.map((row) =>
        combineCommitments(row, BigInt(t.index)),
      ),
    );
    if (!recomputedVk.equals(t.verificationKey)) {
      throw new Error(
        `trustee ${t.id} verificationKey does not match Feldman derivation`,
      );
    }
  }

  return transcript.combinedPublicKey;
}

// ---------------------------------------------------------------------------
// In-process simulator — for tests + R1 smoke
// ---------------------------------------------------------------------------

export interface SimulateInput {
  readonly trustees: ReadonlyArray<Trustee>;
  readonly threshold: number;
}

export interface SimulateOutput {
  readonly transcript: DkgTranscript;
  /** All trustees' secret shares — only meaningful in a simulator. */
  readonly secretShares: ReadonlyArray<TrusteeSecretShare>;
}

/**
 * Run the full happy-path ceremony in one process: sample polynomials,
 * deal shares, verify, finalize. No complaint round (assumes honest
 * dealers). Returns the final transcript plus every trustee's secret
 * share — the latter would be private to each trustee in a real
 * deployment, but the simulator collects all of them so callers can run
 * a tally end-to-end on top.
 */
export function simulateDkg(input: SimulateInput): SimulateOutput {
  validateTrusteeSet(input.trustees, input.threshold);

  // Round 1 per trustee: collect contributions, secrets, and shares.
  const r1: ReadonlyArray<Round1Output> = input.trustees.map((self) =>
    dkgRound1({
      self,
      trustees: input.trustees,
      threshold: input.threshold,
    }),
  );

  // Distribute shares to recipients.
  const sharesPerRecipient = new Map<string, DkgSharePackage[]>();
  for (const t of input.trustees) sharesPerRecipient.set(t.id, []);
  for (const out of r1) {
    for (const pkg of out.sharesForOthers) {
      sharesPerRecipient.get(pkg.toTrusteeId)!.push(pkg);
    }
  }

  // Round 2 per trustee: must yield zero complaints in the happy path.
  for (const self of input.trustees) {
    const verify = dkgRound2Verify({
      self,
      trustees: input.trustees,
      threshold: input.threshold,
      contributions: r1.map((o) => o.contribution),
      receivedShares: sharesPerRecipient.get(self.id)!,
    });
    if (verify.complaints.length > 0) {
      throw new Error(
        `simulateDkg: unexpected complaints from ${self.id}: ${verify.complaints
          .map((c) => `${c.againstTrusteeId}(${c.reason})`)
          .join(", ")}`,
      );
    }
  }

  // Finalize per trustee. Every trustee independently derives the same
  // transcript; we keep the first one and sanity-check the rest match.
  let transcript: DkgTranscript | undefined;
  const secretShares: TrusteeSecretShare[] = [];
  for (let i = 0; i < input.trustees.length; i++) {
    const self = input.trustees[i]!;
    const out = dkgFinalize({
      self,
      qualifiedTrustees: input.trustees,
      threshold: input.threshold,
      contributions: r1.map((o) => o.contribution),
      mySecret: r1[i]!.secret,
      receivedShares: sharesPerRecipient.get(self.id)!,
    });
    if (!transcript) transcript = out.transcript;
    else if (!out.transcript.combinedPublicKey.equals(transcript.combinedPublicKey)) {
      throw new Error(
        `simulateDkg: trustee ${self.id} derived a different combined PK`,
      );
    }
    secretShares.push(out.mySecretShare);
  }

  return { transcript: transcript!, secretShares };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function validateTrusteeSet(
  trustees: ReadonlyArray<Trustee>,
  threshold: number,
): void {
  if (!Number.isInteger(threshold) || threshold < 1) {
    throw new Error("threshold must be a positive integer");
  }
  if (threshold > trustees.length) {
    throw new Error(
      `threshold ${threshold} exceeds trustee count ${trustees.length}`,
    );
  }
  const seenIds = new Set<string>();
  const seenIndices = new Set<number>();
  for (const t of trustees) {
    if (!Number.isInteger(t.index) || t.index < 1) {
      throw new Error(`trustee ${t.id} has non-positive index ${t.index}`);
    }
    if (seenIds.has(t.id)) {
      throw new Error(`duplicate trustee id ${t.id}`);
    }
    if (seenIndices.has(t.index)) {
      throw new Error(`duplicate trustee index ${t.index}`);
    }
    seenIds.add(t.id);
    seenIndices.add(t.index);
  }
}

function sumPoints(points: ReadonlyArray<RistrettoPoint>): RistrettoPoint {
  if (points.length === 0) {
    throw new Error("cannot sum an empty point set");
  }
  let acc = points[0]!;
  for (let i = 1; i < points.length; i++) {
    acc = acc.add(points[i]!);
  }
  return acc;
}
